/**
 * WorkbenchPanel
 *
 * Right-side panel that wraps @ideoz/workbench components for the
 * conversation page. Receives streaming state from usePrototyperWorkbenchFacade
 * and renders the Workbench component with close/toggle controls.
 */

import React, { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  Workbench,
  workbenchStore,
  bootWebContainer,
  retryTemplateMount,
  getWebContainer,
  restartDevServerAfterSnapshot,
} from '@ideoz/workbench';
import { Button, Icon, cn } from '@ideoz/ui';
import { PrototyperLoadingAnimation, PrototyperLoadingIcon } from './PrototyperLoadingAnimation';
import { ChevronsLeft, ChevronsRight, Eye, Loader2 } from 'lucide-react';
import { useTrackEvent } from '@ideoz/core/analytics';

export interface WorkbenchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onToggleConversationPanel?: () => void;
  isConversationPanelCollapsed?: boolean;
  projectName?: string;
  /** Hide outer chrome when nested inside {@link PrototyperPanel} (publish uses legacy header). */
  embedded?: boolean;
  /** Forwarded to the embedded Workbench toolbar so the panel's header merges into it. */
  headerLeftSlot?: React.ReactNode;
  headerRightSlot?: React.ReactNode;
  headerRightLeadSlot?: React.ReactNode;
}

export function WorkbenchPanel({
  isOpen,
  onClose,
  onToggleConversationPanel,
  isConversationPanelCollapsed,
  projectName,
  embedded = false,
  headerLeftSlot,
  headerRightSlot,
  headerRightLeadSlot,
}: WorkbenchPanelProps) {
  const bootStatus = useStore(workbenchStore.bootStatus);
  const bootError = useStore(workbenchStore.bootError);
  const devServerStatus = useStore(workbenchStore.devServerStatus);
  const devServerDead = devServerStatus === 'crashed' || devServerStatus === 'error';
  const { trackEvent } = useTrackEvent();
  const [restarting, setRestarting] = useState(false);

  /**
   * `restartDevServerAfterSnapshot` resolves as soon as the new process spawns, well before
   * we know whether it actually came up — without waiting for a terminal `devServerStatus`
   * too, the button would flip back to idle mid-attempt with no way to tell the user whether
   * it worked. Guards against overlapping clicks queuing multiple restarts.
   */
  const restartPreview = (): void => {
    if (restarting) return;
    const wc = getWebContainer();
    if (!wc) return;
    setRestarting(true);
    void restartDevServerAfterSnapshot(wc)
      .catch(() => {
        /* devServerStatus already reflects the failure — nothing extra to do here */
      })
      .then(
        () =>
          new Promise<void>((resolve) => {
            const unsubscribe = workbenchStore.devServerStatus.subscribe((status) => {
              if (status === 'ready' || status === 'crashed' || status === 'error') {
                unsubscribe();
                resolve();
              }
            });
          })
      )
      .finally(() => setRestarting(false));
  };

  useEffect(() => {
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<{ eventName?: string; properties?: Record<string, unknown> }>;
      const name = ce.detail?.eventName;
      if (!name) return;
      trackEvent(name, ce.detail.properties);
    };
    window.addEventListener('ideoz-hot-edit-analytics', handler);
    return () => window.removeEventListener('ideoz-hot-edit-analytics', handler);
  }, [trackEvent]);

  if (!isOpen) return null;

  const isCrossOriginIsolated = typeof window !== 'undefined' && window.crossOriginIsolated;

  /**
   * Boot/crash gates shared by the embedded and standalone layouts below — same conditions,
   * same markup either way. `Workbench` never mounts while one of these is showing, so there's
   * no risk of them competing with `Workbench`'s own loading-phase ladder for the same moment;
   * this is purely a dedup of what used to be two independently-maintained copies of this JSX.
   * Returns `null` once none of the gates apply, meaning it's time to render `<Workbench/>`.
   */
  const renderBootGate = (): React.ReactNode | null => {
    if (!isCrossOriginIsolated) {
      return (
        <div className="flex items-center justify-center h-full p-8 text-center">
          <div>
            <p className="text-lg font-semibold text-red-600 mb-2">WebContainer unavailable</p>
            <p className="text-sm text-slate-600">
              Cross-origin isolation is required. Ensure COOP/COEP headers are set on this origin.
            </p>
          </div>
        </div>
      );
    }
    if (bootStatus === 'booting' || bootStatus === 'idle') {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          <p className="text-sm text-slate-600 text-center max-w-sm">
            Starting in-browser Node.js (WebContainer)…
          </p>
        </div>
      );
    }
    if (bootStatus === 'error') {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-red-600">Could not start WebContainer</p>
          <p className="text-sm text-slate-600 max-w-md">{bootError || 'Unknown error'}</p>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => {
              void retryTemplateMount().catch(() => {
                void bootWebContainer();
              });
            }}
          >
            Retry
          </Button>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh page
          </Button>
          <p className="text-xs text-slate-500 max-w-sm">
            A full reload clears duplicate boots after dev hot reload or multiple workbench starts.
          </p>
        </div>
      );
    }
    // `devServerStatus` flips to 'starting' the instant a restart begins, which would make
    // `devServerDead` false and drop this whole card (button included) before the user ever
    // sees the outcome — stay mounted through an active restart attempt too, not just while
    // genuinely dead.
    if (devServerDead || restarting) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-red-600">
            {devServerDead ? 'Preview server stopped' : 'Restarting preview server…'}
          </p>
          <p className="text-sm text-slate-600 max-w-md">
            {devServerDead
              ? `The in-browser dev server ${devServerStatus === 'crashed' ? 'crashed' : 'did not respond in time'}. Your files are safe — restart the preview to continue.`
              : 'Your files are safe — this can take a moment.'}
          </p>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={restartPreview}
            disabled={restarting}
          >
            {restarting ? 'Restarting…' : 'Restart preview'}
          </Button>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh page
          </Button>
        </div>
      );
    }
    return null;
  };

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0 h-full bg-slate-50 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderBootGate() ?? (
            <Workbench
              projectName={projectName}
              suppressInspectorRail={embedded}
              headerLeftSlot={headerLeftSlot}
              headerRightSlot={headerRightSlot}
              headerRightLeadSlot={headerRightLeadSlot}
              renderPreviewLoading={({ message, variant }) =>
                variant === 'generating' ? (
                  <PrototyperLoadingAnimation />
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <PrototyperLoadingAnimation />
                    <p className="text-sm text-slate-600 max-w-sm">{message}</p>
                  </div>
                )
              }
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-50">
      {/* Workbench toolbar — match PrototyperPanel toggle styling */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-100 border-b border-slate-200 gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {onToggleConversationPanel && (
            <Button
              type="button"
              onClick={onToggleConversationPanel}
              variant="ghost"
              size="sm"
              className="!p-1 h-auto border border-slate-300 gap-0.5 shrink-0"
              aria-label={
                isConversationPanelCollapsed ? 'Show conversation panel' : 'Hide conversation panel'
              }
            >
              <Icon
                icon={isConversationPanelCollapsed ? ChevronsRight : ChevronsLeft}
                size={24}
                className={cn(
                  'rounded-full p-1',
                  isConversationPanelCollapsed
                    ? 'bg-primary-900 stroke-white'
                    : 'bg-white stroke-primary-900'
                )}
              />
              <Icon
                icon={Eye}
                size={24}
                className={cn(
                  'rounded-full p-1',
                  isConversationPanelCollapsed
                    ? 'bg-white stroke-primary-900'
                    : 'bg-primary-900 stroke-white'
                )}
              />
            </Button>
          )}
          <span className="text-sm font-medium text-slate-800 truncate">
            WebContainer workbench
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1 text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 rounded-md shrink-0"
          title="Close workbench"
        >
          Close
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {renderBootGate() ?? (
          <Workbench
            projectName={projectName}
            renderPreviewLoading={({ message, variant }) =>
              variant === 'generating' ? (
                <PrototyperLoadingAnimation />
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <PrototyperLoadingAnimation />
                  <p className="text-sm text-slate-600 max-w-sm">{message}</p>
                </div>
              )
            }
          />
        )}
      </div>
    </div>
  );
}
